import { supabase } from '../lib/supabase';

export interface Inquiry {
  id: string;
  profile_user_id: string;
  requester_user_id: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  message?: string;
  created_at: string;
  updated_at: string;
  responded_at?: string;
}

export interface InquiryResponse {
  success: boolean;
  error?: string;
  inquiry_id?: string;
  message?: string;
}

export class InquiryService {
  /**
   * Create an inquiry to view a private profile
   */
  static async createInquiry(targetProfileId: string, message?: string): Promise<InquiryResponse> {
    try {
      if (!targetProfileId) {
        return { success: false, error: 'Target profile ID is required' };
      }

      console.log('InquiryService: Creating inquiry for profile:', targetProfileId);
      
      const { data, error } = await supabase.rpc('create_inquiry', {
        target_profile_id: targetProfileId,
        inquiry_message: message || null
      });

      if (error) {
        console.error('InquiryService: Error creating inquiry:', error);
        return { 
          success: false, 
          error: error.message || 'Failed to create inquiry' 
        };
      }

      console.log('InquiryService: Inquiry created successfully:', data);
      return data;
    } catch (error) {
      console.error('InquiryService: Exception creating inquiry:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to create inquiry' 
      };
    }
  }

  /**
   * Respond to an inquiry (approve or reject)
   */
  static async respondToInquiry(inquiryId: string, status: 'approved' | 'rejected' | 'pending'): Promise<InquiryResponse> {
    try {
      if (!inquiryId || !status) {
        return { success: false, error: 'Inquiry ID and status are required' };
      }

      console.log('InquiryService: Responding to inquiry:', { inquiryId, status });
      
      const { data, error } = await supabase.rpc('respond_to_inquiry', {
        inquiry_id: inquiryId,
        new_status: status
      });

      if (error) {
        console.error('InquiryService: Error responding to inquiry:', error);
        return { 
          success: false, 
          error: error.message || 'Failed to respond to inquiry' 
        };
      }

      console.log('InquiryService: Inquiry responded successfully:', data);
      return data;
    } catch (error) {
      console.error('InquiryService: Exception responding to inquiry:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to respond to inquiry' 
      };
    }
  }

  /**
   * Check if user can view a profile
   */
  static async canViewProfile(profileId: string, viewerId: string): Promise<boolean> {
    try {
      if (!profileId || !viewerId) {
        return false;
      }

      console.log('InquiryService: Checking if user can view profile:', { profileId, viewerId });
      
      const { data, error } = await supabase.rpc('can_view_profile', {
        profile_id: profileId,
        viewer_id: viewerId
      });

      if (error) {
        console.error('InquiryService: Error checking profile access:', error);
        return false;
      }

      console.log('InquiryService: Can view profile:', data);
      return data || false;
    } catch (error) {
      console.error('InquiryService: Exception checking profile access:', error);
      return false;
    }
  }

  /**
   * Get inquiries for a profile owner
   */
  static async getInquiriesForProfile(profileId: string): Promise<Inquiry[]> {
    try {
      if (!profileId) {
        return [];
      }

      console.log('InquiryService: Getting inquiries for profile:', profileId);
      
      const { data, error } = await supabase
        .from('inquiries')
        .select(`
          id,
          profile_user_id,
          requester_user_id,
          status,
          message,
          created_at,
          updated_at,
          responded_at
        `)
        .eq('profile_user_id', profileId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('InquiryService: Error fetching inquiries:', error);
        return [];
      }

      console.log('InquiryService: Found inquiries:', data?.length || 0);
      return data || [];
    } catch (error) {
      console.error('InquiryService: Exception fetching inquiries:', error);
      return [];
    }
  }

  /**
   * Get inquiries made by a user
   */
  static async getInquiriesByUser(userId: string): Promise<Inquiry[]> {
    try {
      if (!userId) {
        return [];
      }

      console.log('InquiryService: Getting inquiries by user:', userId);
      
      const { data, error } = await supabase
        .from('inquiries')
        .select(`
          id,
          profile_user_id,
          requester_user_id,
          status,
          message,
          created_at,
          updated_at,
          responded_at
        `)
        .eq('requester_user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('InquiryService: Error fetching user inquiries:', error);
        return [];
      }

      console.log('InquiryService: Found user inquiries:', data?.length || 0);
      return data || [];
    } catch (error) {
      console.error('InquiryService: Exception fetching user inquiries:', error);
      return [];
    }
  }

  /**
   * Check if user has a pending inquiry for a profile
   */
  static async hasPendingInquiry(profileId: string, userId: string): Promise<boolean> {
    try {
      if (!profileId || !userId) {
        return false;
      }

      console.log('InquiryService: Checking pending inquiry:', { profileId, userId });
      
      const { data, error } = await supabase
        .from('inquiries')
        .select('id')
        .eq('profile_user_id', profileId)
        .eq('requester_user_id', userId)
        .eq('status', 'pending')
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('InquiryService: Error checking pending inquiry:', error);
        return false;
      }

      const hasPending = !!data;
      console.log('InquiryService: Has pending inquiry:', hasPending);
      return hasPending;
    } catch (error) {
      console.error('InquiryService: Exception checking pending inquiry:', error);
      return false;
    }
  }
}
